import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DateRangePicker, { DEFAULT_DATE_PRESET, useDateRangePickerState } from '../components/DateRangePicker';
import { EventEmitterType, useEventEmitterContext } from '../context/event-emitter.js';
import GeneralAnalyticsReportComponent, {
  buildGeneralAnalyticsCsvData,
  buildGeneralAnalyticsCsvFilename,
  buildGeneralAnalyticsReportData,
  buildSurveyQuestionOrder,
  buildSurveyReportRows,
  downloadCsvText,
  SURVEY_REPORT_CSV_OPTIONS,
} from './GeneralAnalyticsReportComponent';
import { agentApi, analysisReportsAPI } from '../services/api';
import './Analytics.css';
import './GeneralAnalytics.css';
import './SurveyAnalytics.css';

const HOSPITAL_ANALYSIS_ENDPOINT = '/api/analysis-hospital-generate';
const GENERAL_ANALYSIS_ENDPOINT = '/api/analysis-general-generate';
const SURVEY_ANALYSIS_ENDPOINT = '/api/analysis-survey-generate';
const EXCLUDED_ANALYSIS_TYPES = new Set(['none']);
const HOSPITAL_ENDPOINT_TYPES = new Set(['hospital-scheduling', 'hospital-test-results']);
const ANALYTICS_SETUP_SCRIPT_TAB = 'script-setup';
const SURVEY_SERVICE_TEST_CONFIG = {
  totalQuestions: 5,
  onlyPhoneCalls: true,
  showCallType: false,
};
const SURVEY_SCRIPT_CONFIG = {
  'survey service test': SURVEY_SERVICE_TEST_CONFIG,
  'opinion service test': SURVEY_SERVICE_TEST_CONFIG,
  'voxco test 11/12': {
    totalQuestions: 10,
    questionOrder: ['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6', 'Q7', 'Q8', 'Q9', 'Q10'],
    onlyPhoneCalls: false,
    showCallType: true,
  },
};
const COMPLETION_BUCKETS = [0, 20, 40, 60, 80, 100];

const isPhoneCall = (log) => {
  const callId = log.callSid || log.callId || '';
  return String(callId).startsWith('CA');
};

const shouldSuggestAnalyticsSetup = (message) =>
  /analytics setup/i.test(String(message || ''));
const normalizeScriptOptionName = (value) => String(value || '').trim().toLowerCase();
const normalizeScriptName = (value) => String(value || '').trim().toLowerCase();

const normalizeScriptId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (value && typeof value === 'object' && typeof value.$oid === 'string') {
    return value.$oid.trim();
  }
  const text = String(value).trim();
  return text === '[object Object]' ? '' : text;
};

const formatAnalysisTypeLabel = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized || normalized === 'none') return 'Not set';
  if (normalized === 'hospital-scheduling') return 'Hospital Scheduling';
  if (normalized === 'hospital-test-results') return 'Hospital Test Results';
  return normalized
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const buildPercent = (count, total) => {
  if (!total) return 0;
  return Math.round((count / total) * 1000) / 10;
};

const isNotMentioned = (value) =>
  typeof value === 'string' && value.trim().toLowerCase() === 'not mentioned';

const getCallTypeLabel = (log) => {
  const callId = String(log?.callSid || log?.callId || '');
  if (!callId) return 'Unknown';
  if (callId === 'voiceAgent') return 'Voice agent';
  if (callId === 'chatbot') return 'Chatbot';
  if (callId.startsWith('CA')) return 'Phone call';
  return 'Unknown';
};

const getSurveyScriptConfig = (scriptName) =>
  SURVEY_SCRIPT_CONFIG[normalizeScriptName(scriptName)] || null;

const getTotalQuestionsForStats = (stats, scriptName) => {
  const scriptConfig = getSurveyScriptConfig(scriptName);
  if (scriptConfig?.totalQuestions) return scriptConfig.totalQuestions;
  if (!stats) return 0;

  return Math.max(
    0,
    Object.values(stats).reduce((maxCount, stat) => {
      const questionNumber = String(stat.questionNumber || '').replace(/^Q/i, '');
      const parsed = Number(questionNumber);
      if (!Number.isNaN(parsed) && parsed > maxCount) return parsed;
      const answerCount = stat.total || 0;
      return Math.max(maxCount, answerCount);
    }, 0)
  );
};

const formatSavedReportDateRange = (dateRange) => {
  if (!dateRange?.from || !dateRange?.to) return '';

  try {
    return `${new Date(dateRange.from).toLocaleDateString()} - ${new Date(dateRange.to).toLocaleDateString()}`;
  } catch {
    return '';
  }
};

const GeneralAnalyticsComponent = ({
  initialSelection,
  onSelectionApplied,
  embeddedInWizard = false,
  fixedScriptSelection = null,
  hideConfigureButton = false,
  allowedScriptId = null,
}) => {
  const [logs, setLogs] = useState([]);
  const [scriptOptions, setScriptOptions] = useState([]);
  const [scriptsReady, setScriptsReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedScriptId, setSelectedScriptId] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [, setHospitalStats] = useState(null);
  const [, setSummaryStats] = useState(null);
  const [, setSurveyStats] = useState(null);
  const [, setSentimentStats] = useState(null);
  const [, setCompletionStats] = useState(null);
  const [expandedIds, setExpandedIds] = useState({});
  const [analysisResults, setAnalysisResults] = useState({});
  const [surveyResults, setSurveyResults] = useState({});
  const [batchProgress, setBatchProgress] = useState({ processed: 0, total: 0 });
  const [generatePulse, setGeneratePulse] = useState(false);
  const [downloadPulse, setDownloadPulse] = useState(false);
  const [showCallDetails, setShowCallDetails] = useState(false);
  const [activeReportView, setActiveReportView] = useState(null);
  const [savedReportsOpen, setSavedReportsOpen] = useState(false);
  const [savedReportsLoading, setSavedReportsLoading] = useState(false);
  const [savedReportsError, setSavedReportsError] = useState('');
  const [savedReports, setSavedReports] = useState([]);
  const [cachedReports, setCachedReports] = useState({});
  const [viewingReportId, setViewingReportId] = useState('');
  const [downloadingReportAction, setDownloadingReportAction] = useState('');
  const [deleteReportState, setDeleteReportState] = useState({
    open: false,
    report: null,
    deleting: false,
  });
  const reportAnchorRef = useRef(null);
  const [noticeModal, setNoticeModal] = useState({
    open: false,
    title: '',
    message: '',
    showSetupAction: false,
    setupScriptId: '',
    setupScriptName: '',
  });
  const { eventEmitter } = useEventEmitterContext();
  const {
    selectedPreset: selectedDatePreset,
    startDateInput,
    endDateInput,
    resolvedDateRange,
    error: dateRangeError,
    applyPreset,
    handleStartDateChange,
    handleEndDateChange,
  } = useDateRangePickerState(DEFAULT_DATE_PRESET);

  const fixedScriptId = useMemo(
    () => normalizeScriptId(fixedScriptSelection?.scriptId),
    [fixedScriptSelection?.scriptId]
  );
  const fixedScriptName = useMemo(
    () => String(fixedScriptSelection?.scriptName || '').trim(),
    [fixedScriptSelection?.scriptName]
  );
  const fixedScriptType = useMemo(
    () => String(fixedScriptSelection?.scriptType || '').trim().toLowerCase(),
    [fixedScriptSelection?.scriptType]
  );

  const selectableScriptOptions = useMemo(() => {
    if (!embeddedInWizard || !fixedScriptId) {
      return scriptOptions;
    }

    if (scriptOptions.some((option) => option.id === fixedScriptId)) {
      return scriptOptions;
    }

    return [
      {
        id: fixedScriptId,
        name: fixedScriptName || 'Selected script',
        type: fixedScriptType,
      },
      ...scriptOptions,
    ];
  }, [embeddedInWizard, fixedScriptId, fixedScriptName, fixedScriptType, scriptOptions]);

  const selectedScript = useMemo(
    () => (
      selectableScriptOptions.find((option) => option.id === selectedScriptId)
      || (embeddedInWizard && fixedScriptId && selectedScriptId === fixedScriptId
        ? {
          id: fixedScriptId,
          name: fixedScriptName || 'Selected script',
          type: fixedScriptType,
        }
        : null)
    ),
    [
      embeddedInWizard,
      fixedScriptId,
      fixedScriptName,
      fixedScriptType,
      selectableScriptOptions,
      selectedScriptId,
    ]
  );
  const selectedScriptName = selectedScript?.name || '';
  const selectedScriptType = String(selectedScript?.type || '').trim().toLowerCase();
  const selectedEngine = HOSPITAL_ENDPOINT_TYPES.has(selectedScriptType) ? 'hospital' : 'general';
  const isSurveyType = selectedScriptType === 'survey';
  const shouldShowConfigureButton = !embeddedInWizard && !hideConfigureButton;

  const resetAnalysisState = useCallback(() => {
    setHospitalStats(null);
    setSummaryStats(null);
    setSurveyStats(null);
    setSentimentStats(null);
    setCompletionStats(null);
    setActiveReportView(null);
    setBatchProgress({ processed: 0, total: 0 });
    setShowCallDetails(false);
    setDownloadPulse(false);
  }, []);

  useEffect(() => {
    if (!noticeModal.open) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setNoticeModal({
          open: false,
          title: '',
          message: '',
          showSetupAction: false,
          setupScriptId: '',
          setupScriptName: '',
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [noticeModal.open]);

  const closeNoticeModal = () => {
    setNoticeModal({
      open: false,
      title: '',
      message: '',
      showSetupAction: false,
      setupScriptId: '',
      setupScriptName: '',
    });
  };

  const openNoticeModal = ({ title, message, setupScriptId, setupScriptName }) => {
    const normalizedMessage = String(message || '').trim();
    setNoticeModal({
      open: true,
      title: String(title || 'Notice'),
      message: normalizedMessage || 'Something went wrong. Please try again.',
      showSetupAction: shouldSuggestAnalyticsSetup(normalizedMessage),
      setupScriptId: String(setupScriptId || '').trim(),
      setupScriptName: String(setupScriptName || '').trim(),
    });
  };

  const goToAnalyticsSetup = () => {
    emitAnalyticsSetupNavigation({
      scriptId: noticeModal.setupScriptId || '',
      scriptName: noticeModal.setupScriptName || selectedScriptName || '',
    });
    closeNoticeModal();
  };

  const emitAnalyticsSetupNavigation = ({ scriptId, scriptName }) => {
    const setupPayload = {
      scriptId: String(scriptId || '').trim(),
      scriptName: String(scriptName || '').trim(),
      tab: ANALYTICS_SETUP_SCRIPT_TAB,
    };
    eventEmitter?.emit({
      type: EventEmitterType.DashboardSelectMenu,
      payload: {
        menuItem: 'Analytics Setup',
        params: setupPayload,
      },
    });
  };

  const handleConfigureScriptSetup = () => {
    emitAnalyticsSetupNavigation({
      scriptId: selectedScriptId,
      scriptName: selectedScriptName,
    });
  };

  useEffect(() => {
    let isMounted = true;
    setScriptsReady(false);

    const fetchHospitalScripts = async () => {
      try {
        const { data } = await agentApi.get('/api/analysis-scripts');
        if (!isMounted) return;

        if (!data?.success) {
          setScriptOptions([]);
          setSelectedScriptId('');
          return;
        }

        const options = (Array.isArray(data?.scripts) ? data.scripts : [])
          .map((script) => {
            const type = String(script?.settings?.type || '').trim().toLowerCase();
            return {
              id: normalizeScriptId(script?.scriptId || script?.id),
              name: String(script?.scriptName || script?.name || 'Untitled script').trim() || 'Untitled script',
              type,
            };
          })
          .filter((option) => option.id && option.type && !EXCLUDED_ANALYSIS_TYPES.has(option.type))
          .filter((option) => !allowedScriptId || option.id === allowedScriptId)
          .sort((a, b) => a.name.localeCompare(b.name));

        setScriptOptions(options);
        setSelectedScriptId((currentId) => {
          if (embeddedInWizard) {
            return fixedScriptId || '';
          }
          if (currentId && options.some((option) => option.id === currentId)) {
            return currentId;
          }
          return options[0]?.id || '';
        });
      } catch (err) {
        if (!isMounted) return;
        console.error('Failed to fetch general analytics scripts:', err);
        setScriptOptions([]);
        setSelectedScriptId('');
      } finally {
        if (isMounted) {
          setScriptsReady(true);
        }
      }
    };

    fetchHospitalScripts();

    return () => {
      isMounted = false;
    };
  }, [embeddedInWizard, fixedScriptId, allowedScriptId]);

  useEffect(() => {
    if (!embeddedInWizard) return;

    setSelectedScriptId((currentId) => (currentId === fixedScriptId ? currentId : fixedScriptId));
    resetAnalysisState();
  }, [embeddedInWizard, fixedScriptId, resetAnalysisState]);

  useEffect(() => {
    if (embeddedInWizard) return;
    if (!initialSelection || !scriptsReady) return;

    const incomingId = String(initialSelection?.scriptId || '').trim();
    const incomingName = String(initialSelection?.scriptName || '').trim();

    let targetOption = null;
    if (incomingId) {
      targetOption = scriptOptions.find((option) => option.id === incomingId) || null;
    }

    if (!targetOption && incomingName) {
      const normalizedIncomingName = normalizeScriptOptionName(incomingName);
      targetOption = scriptOptions.find(
        (option) => normalizeScriptOptionName(option.name) === normalizedIncomingName
      ) || null;
    }

    if (targetOption?.id) {
      setSelectedScriptId(targetOption.id);
    }

    if (initialSelection?.highlightGenerate) {
      setGeneratePulse(true);
    }

    onSelectionApplied?.();
  }, [embeddedInWizard, initialSelection, onSelectionApplied, scriptOptions, scriptsReady]);

  useEffect(() => {
    if (!generatePulse) return;
    const timer = window.setTimeout(() => setGeneratePulse(false), 3500);
    return () => window.clearTimeout(timer);
  }, [generatePulse]);

  useEffect(() => {
    if (!scriptsReady) return;

    resetAnalysisState();

    if (!selectedScriptId || dateRangeError || !resolvedDateRange) {
      setLogs([]);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);

    const fetchLogs = async () => {
      try {
        const { data } = await agentApi.get('/api/agentlogs/by-script', {
          params: {
            scriptId: selectedScriptId,
            from: resolvedDateRange.start.toISOString(),
            to: resolvedDateRange.end.toISOString(),
          },
        });

        if (!isMounted) return;

        if (data?.success) {
          setLogs(Array.isArray(data.logs) ? data.logs : []);
        } else {
          console.error('Failed to fetch analytics logs');
          setLogs([]);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error fetching analytics logs:', err);
        setLogs([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchLogs();

    return () => {
      isMounted = false;
    };
  }, [dateRangeError, resetAnalysisState, resolvedDateRange, scriptsReady, selectedScriptId]);

  const fetchHospitalAnalysis = async (log, scriptId, analysisType) => {
    const resolvedScriptId = normalizeScriptId(scriptId || log.scriptId);
    const normalizedType = String(analysisType || '').trim().toLowerCase();
    const endpoint = HOSPITAL_ENDPOINT_TYPES.has(normalizedType)
      ? HOSPITAL_ANALYSIS_ENDPOINT
      : GENERAL_ANALYSIS_ENDPOINT;
    if (!resolvedScriptId) {
      throw new Error(
        'Missing scriptId for this call log. Please refresh and retry.'
      );
    }

    const { data } = await agentApi.post(endpoint, {
        transcript: log.transcript,
        logId: log._id,
        scriptId: resolvedScriptId,
    });
    if (!data.success) {
      throw new Error(data.message || 'Unknown error');
    }
    return data.analysis;
  };

  const fetchSurveyAnalysis = async (log, scriptId) => {
    const resolvedScriptId = normalizeScriptId(scriptId || log.scriptId);
    if (!resolvedScriptId) {
      throw new Error(
        'Missing scriptId for this call log. Please refresh and retry.'
      );
    }

    const { data } = await agentApi.post(SURVEY_ANALYSIS_ENDPOINT, {
        transcript: log.transcript,
        logId: log._id,
        scriptId: resolvedScriptId,
    });
    if (!data.success) {
      throw new Error(data.message || 'Unknown error');
    }
    return data.analysis;
  };

  const surveyCalls = useMemo(() => {
    if (!selectedScriptId || !isSurveyType) return [];
    const scriptConfig = getSurveyScriptConfig(selectedScriptName);

    return logs.filter((log) => {
      const logScriptId = normalizeScriptId(log.scriptId);
      if (logScriptId !== selectedScriptId) return false;
      if (!scriptConfig?.onlyPhoneCalls) return true;
      const callId = String(log.callSid || log.callId || '');
      return callId.startsWith('CA');
    });
  }, [isSurveyType, logs, selectedScriptId, selectedScriptName]);

  const generalCalls = useMemo(() => {
    if (!selectedScriptId || isSurveyType) return [];
    return logs.filter((log) => {
      const logScriptId = normalizeScriptId(log.scriptId);
      return logScriptId === selectedScriptId && isPhoneCall(log);
    });
  }, [isSurveyType, logs, selectedScriptId]);

  const activeCalls = isSurveyType ? surveyCalls : generalCalls;

  const showSurveyCallType = useMemo(() => {
    const scriptConfig = getSurveyScriptConfig(selectedScriptName);
    return scriptConfig?.showCallType !== false;
  }, [selectedScriptName]);

  const cacheReportDetail = useCallback((report) => {
    if (!report?.id) return;
    setCachedReports((currentReports) => ({ ...currentReports, [report.id]: report }));
  }, []);

  const loadSavedReports = useCallback(async () => {
    if (!selectedScriptId) {
      setSavedReports([]);
      setSavedReportsError('');
      return;
    }

    setSavedReportsLoading(true);
    setSavedReportsError('');

    try {
      const { data } = await analysisReportsAPI.list(selectedScriptId);
      if (!data?.success) {
        throw new Error(data?.message || 'Failed to load saved reports.');
      }

      setSavedReports(Array.isArray(data.reports) ? data.reports : []);
    } catch (err) {
      console.error('Failed to load analysis reports:', err);
      setSavedReports([]);
      setSavedReportsError(err?.message || 'Failed to load saved reports.');
    } finally {
      setSavedReportsLoading(false);
    }
  }, [selectedScriptId]);

  const loadReportDetail = useCallback(async (reportId) => {
    if (!reportId) {
      throw new Error('Missing reportId.');
    }

    if (cachedReports[reportId]) {
      return cachedReports[reportId];
    }

    const { data } = await analysisReportsAPI.get(reportId);
    if (!data?.success || !data?.report) {
      throw new Error(data?.message || 'Failed to load report.');
    }

    cacheReportDetail(data.report);
    return data.report;
  }, [cacheReportDetail, cachedReports]);

  const saveGeneratedReport = useCallback(async ({ reportData, csvData }) => {
    const { data } = await analysisReportsAPI.create({
      scriptId: selectedScriptId,
      reportData,
      csvData,
    });

    if (!data?.success || !data?.report) {
      throw new Error(data?.message || 'Failed to save report.');
    }

    const savedReport = {
      ...data.report,
      reportData,
      csvData,
    };

    cacheReportDetail(savedReport);
    if (savedReportsOpen) {
      setSavedReports((currentReports) => [data.report, ...currentReports.filter((report) => report.id !== data.report.id)]);
    }

    return data.report;
  }, [cacheReportDetail, savedReportsOpen, selectedScriptId]);

  useEffect(() => {
    if (!savedReportsOpen) return;
    loadSavedReports();
  }, [loadSavedReports, savedReportsOpen]);

  useEffect(() => {
    if (activeReportView?.source !== 'saved' || !reportAnchorRef.current) return;

    const frameId = window.requestAnimationFrame(() => {
      reportAnchorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [activeReportView]);

  const handleGenerateStats = async () => {
    if (!selectedScriptId) {
      setBatchProgress({ processed: 0, total: 0 });
      openNoticeModal({
        title: 'No Script Selected',
        message: 'No script is configured. Please go to "Analytics Setup" and set a script analysis type.',
      });
      return;
    }

    if (!activeCalls.length) {
      setBatchProgress({ processed: 0, total: 0 });
      openNoticeModal({
        title: 'No Calls to Analyze',
        message: 'No calls are available for the selected script yet.',
        setupScriptId: selectedScriptId,
        setupScriptName: selectedScriptName,
      });
      return;
    }

    setBatchLoading(true);
    resetAnalysisState();
    setDownloadPulse(false);
    setBatchProgress({ processed: 0, total: activeCalls.length });

    try {
      if (isSurveyType) {
        const aggregated = {};
        const sentimentCounts = {
          positive: 0,
          neutral: 0,
          negative: 0,
          unknown: 0,
        };
        const cache = { ...surveyResults };

        for (const log of surveyCalls) {
          setBatchProgress((prev) => ({
            ...prev,
            processed: Math.min(prev.processed + 1, prev.total || surveyCalls.length),
          }));
          if (!log.transcript) continue;

          let analysis = cache[log._id];
          if (!analysis) {
            analysis = await fetchSurveyAnalysis(log, selectedScriptId);
            cache[log._id] = analysis;
          }

          const sentiment = (analysis.sentiment || '').toString().trim().toLowerCase();
          if (sentimentCounts[sentiment] !== undefined) {
            sentimentCounts[sentiment] += 1;
          } else {
            sentimentCounts.unknown += 1;
          }

          (analysis.questions || []).forEach((q) => {
            const qKey = q.questionNumber || q.question || 'Unknown';
            if (!aggregated[qKey]) {
              aggregated[qKey] = {
                questionNumber: q.questionNumber,
                question: q.question,
                total: 0,
                answers: {},
              };
            }
            aggregated[qKey].total += 1;
            const answerKey = q.answer || `Option ${q.answerCode || 'N/A'}`;
            aggregated[qKey].answers[answerKey] =
              (aggregated[qKey].answers[answerKey] || 0) + 1;
          });
        }

        setSurveyResults((prev) => ({ ...prev, ...cache }));
        setSurveyStats(aggregated);
        setSentimentStats(sentimentCounts);

        const totalQuestionsCount = getTotalQuestionsForStats(aggregated, selectedScriptName);
        const completionBuckets = COMPLETION_BUCKETS.reduce((accumulator, percent) => {
          accumulator[percent] = 0;
          return accumulator;
        }, {});

        surveyCalls.forEach((log) => {
          const analysis = cache[log._id];
          if (!analysis) return;

          const uniqueQuestions = new Set(
            (analysis.questions || [])
              .filter((q) => !isNotMentioned(q.answer))
              .map((q) => (
                q.questionNumber
                ?? q.question
                ?? q.answerCode
                ?? q.answer
                ?? 'Unknown'
              ))
          ).size;

          const answeredCount = totalQuestionsCount
            ? Math.min(uniqueQuestions, totalQuestionsCount)
            : uniqueQuestions;
          const percent = totalQuestionsCount
            ? Math.round((answeredCount / totalQuestionsCount) * 100)
            : 0;

          completionBuckets[percent] = (completionBuckets[percent] || 0) + 1;
        });
        const resolvedQuestionOrder = buildSurveyQuestionOrder(selectedScriptName, aggregated);
        const reportData = buildGeneralAnalyticsReportData({
          reportType: 'survey',
          scriptId: selectedScriptId,
          scriptName: selectedScriptName,
          scriptType: selectedScriptType,
          engine: selectedEngine,
          dateRange: resolvedDateRange,
          generatedAt: new Date().toISOString(),
          surveyStats: aggregated,
          sentimentStats: sentimentCounts,
          completionStats: completionBuckets,
          questionOrder: resolvedQuestionOrder,
          surveyRows: buildSurveyReportRows({
            surveyCalls,
            surveyResults: cache,
          }),
        });
        const csvData = buildGeneralAnalyticsCsvData(reportData);

        setCompletionStats(completionBuckets);
        setActiveReportView({
          source: 'generated',
          reportId: '',
          reportData,
          csvData,
        });
        setBatchProgress((prev) => ({
          ...prev,
          processed: prev.total || surveyCalls.length,
        }));
        if (surveyCalls.length) {
          setDownloadPulse(true);
          window.setTimeout(() => setDownloadPulse(false), 3500);
        }
        try {
          const savedReport = await saveGeneratedReport({ reportData, csvData });
          setActiveReportView((currentReportView) => (
            currentReportView?.reportData === reportData
              ? { ...currentReportView, reportId: savedReport.id }
              : currentReportView
          ));
        } catch (saveErr) {
          console.error('Failed to save generated survey report:', saveErr);
          openNoticeModal({
            title: 'Report Save Failed',
            message: `Analysis completed, but saving this report failed: ${saveErr?.message || 'Unknown error.'}`,
            setupScriptId: selectedScriptId,
            setupScriptName: selectedScriptName,
          });
        }
        return;
      }

      const sentimentTotals = { Positive: 0, Neutral: 0, Negative: 0, Unknown: 0 };
      const scheduledTotals = { Scheduled: 0, 'Not scheduled': 0 };
      const informedTotals = { 'Informed successfully': 0, 'Not informed': 0 };
      const extraQuestionsTotals = { 'Asked extra questions': 0, 'No extra questions': 0 };
      const scheduleRequestTotals = { 'Requested scheduling': 0, 'No scheduling request': 0 };
      const humanRequestTotals = { 'Requested a human': 0, 'Stayed with AI': 0 };

      const cache = { ...analysisResults };
      let analyzedCount = 0;

      for (const log of generalCalls) {
        setBatchProgress((prev) => ({
          ...prev,
          processed: Math.min(prev.processed + 1, prev.total || generalCalls.length),
        }));
        if (!log.transcript) continue;

        let analysis = cache[log._id];
        if (!analysis) {
          analysis = await fetchHospitalAnalysis(log, selectedScriptId, selectedScriptType);
          cache[log._id] = analysis;
        }

        analyzedCount += 1;

        const extraQuestions = Boolean(analysis.extra_questions);
        const humanRequest = Boolean(analysis.requested_human);
        const sentiment = String(analysis.sentiment || '').toLowerCase();
        const scheduled = Boolean(analysis.scheduled_appointment);
        const informed = Boolean(
          analysis.informed_successfully ?? analysis.informed ?? analysis.scheduled_appointment
        );
        const scheduleRequest = Boolean(
          analysis.requested_scheduling ?? analysis.schedule_request ?? analysis.scheduled_appointment
        );

        scheduledTotals[scheduled ? 'Scheduled' : 'Not scheduled'] += 1;
        informedTotals[informed ? 'Informed successfully' : 'Not informed'] += 1;
        extraQuestionsTotals[extraQuestions ? 'Asked extra questions' : 'No extra questions'] += 1;
        scheduleRequestTotals[scheduleRequest ? 'Requested scheduling' : 'No scheduling request'] += 1;
        humanRequestTotals[humanRequest ? 'Requested a human' : 'Stayed with AI'] += 1;

        if (sentiment === 'positive') sentimentTotals.Positive += 1;
        else if (sentiment === 'neutral') sentimentTotals.Neutral += 1;
        else if (sentiment === 'negative') sentimentTotals.Negative += 1;
        else sentimentTotals.Unknown += 1;
      }

      if (!analyzedCount) {
        setBatchProgress({ processed: 0, total: 0 });
        openNoticeModal({
          title: 'No Analyzable Calls',
          message: 'No analyzable calls found for the selected script.',
          setupScriptId: selectedScriptId,
          setupScriptName: selectedScriptName,
        });
        return;
      }

      setAnalysisResults((prev) => ({ ...prev, ...cache }));
      const totalCalls = analyzedCount;
      let resolvedHospitalStats = null;
      let resolvedSummaryStats = null;

      if (selectedScriptType !== 'hospital-scheduling') {
        resolvedHospitalStats = {
          informed: {
            title: 'Successfully Informed',
            description: 'Shows if the AI agent clearly communicated the test results.',
            answers: informedTotals,
          },
          extraQuestions: {
            title: 'Extra Questions',
            description: 'Flags if respondents asked additional questions about their results.',
            answers: extraQuestionsTotals,
          },
          scheduleRequest: {
            title: 'Request to Schedule Doctor Meeting',
            description: 'Tracks if respondents asked to schedule a follow-up appointment.',
            answers: scheduleRequestTotals,
          },
          humanRequest: {
            title: 'Requests for a Real Human',
            description: 'Highlights when respondents ask to switch from AI to a live agent.',
            answers: humanRequestTotals,
          },
          sentiment: {
            title: 'Sentiment Analysis',
            description: 'Overall tone of the call based on the respondent responses.',
            answers: sentimentTotals,
          },
        };

        resolvedSummaryStats = {
          totalCalls,
          primaryRateLabel: 'Informed Successfully',
          primaryRateValue: buildPercent(informedTotals['Informed successfully'], totalCalls),
          secondaryRateLabel: 'Extra Questions',
          secondaryRateValue: buildPercent(extraQuestionsTotals['Asked extra questions'], totalCalls),
          tertiaryRateLabel: 'Scheduling Requests',
          tertiaryRateValue: buildPercent(scheduleRequestTotals['Requested scheduling'], totalCalls),
          quaternaryRateLabel: 'Human Requests',
          quaternaryRateValue: buildPercent(humanRequestTotals['Requested a human'], totalCalls),
        };
      } else {
        resolvedHospitalStats = {
          scheduled: {
            title: 'Scheduled Appointment',
            description: 'Tracks whether a medical appointment was scheduled during the call.',
            answers: scheduledTotals,
          },
          extraQuestions: {
            title: 'Extra Questions',
            description: 'Flags if respondents asked additional questions about scheduling or coverage.',
            answers: extraQuestionsTotals,
          },
          humanRequest: {
            title: 'Requests for a Real Human',
            description: 'Highlights when respondents ask to switch from AI to a live agent.',
            answers: humanRequestTotals,
          },
          sentiment: {
            title: 'Sentiment Analysis',
            description: 'Overall tone of the call based on the respondent responses.',
            answers: sentimentTotals,
          },
        };

        resolvedSummaryStats = {
          totalCalls,
          primaryRateLabel: 'Scheduled Rate',
          primaryRateValue: buildPercent(scheduledTotals.Scheduled, totalCalls),
          secondaryRateLabel: 'Extra Questions',
          secondaryRateValue: buildPercent(extraQuestionsTotals['Asked extra questions'], totalCalls),
          tertiaryRateLabel: 'Human Requests',
          tertiaryRateValue: buildPercent(humanRequestTotals['Requested a human'], totalCalls),
        };
      }

      setHospitalStats(resolvedHospitalStats);
      setSummaryStats(resolvedSummaryStats);

      const reportData = buildGeneralAnalyticsReportData({
        reportType: 'general',
        scriptId: selectedScriptId,
        scriptName: selectedScriptName,
        scriptType: selectedScriptType,
        engine: selectedEngine,
        dateRange: resolvedDateRange,
        generatedAt: new Date().toISOString(),
        summaryStats: resolvedSummaryStats,
        hospitalStats: resolvedHospitalStats,
      });
      const csvData = buildGeneralAnalyticsCsvData(reportData);

      setActiveReportView({
        source: 'generated',
        reportId: '',
        reportData,
        csvData,
      });

      try {
        const savedReport = await saveGeneratedReport({ reportData, csvData });
        setActiveReportView((currentReportView) => (
          currentReportView?.reportData === reportData
            ? { ...currentReportView, reportId: savedReport.id }
            : currentReportView
        ));
      } catch (saveErr) {
        console.error('Failed to save generated report:', saveErr);
        openNoticeModal({
          title: 'Report Save Failed',
          message: `Analysis completed, but saving this report failed: ${saveErr?.message || 'Unknown error.'}`,
          setupScriptId: selectedScriptId,
          setupScriptName: selectedScriptName,
        });
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to generate stats.';
      console.error('Batch analysis failed:', err);
      setBatchProgress({ processed: 0, total: 0 });
      openNoticeModal({
        title: 'Unable to Generate Stats',
        message,
        setupScriptId: selectedScriptId,
        setupScriptName: selectedScriptName,
      });
      return;
    } finally {
      setBatchLoading(false);
    }
  };

  const toggleTranscript = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderCallCards = () => (
    activeCalls.map((log) => {
      const callId = log.callSid || log.callId;
      return (
        <div key={log._id} className="call-card">
          <div className="call-header">
            <div>
              <div className="call-title">{log.scriptName || log.script || selectedScriptName || 'Unknown script'}</div>
              <div className="call-date">{new Date(log.createdAt).toLocaleString()}</div>
            </div>
            {isSurveyType && showSurveyCallType && (
              <div className="call-id-block">
                <div className="call-id-label">Call type</div>
                <div className="call-id">{getCallTypeLabel(log)}</div>
              </div>
            )}
            <div className="call-id-block">
              <div className="call-id-label">Call ID</div>
              <div className="call-id">{callId}</div>
            </div>
          </div>

          {log.summary && (
            <p className="summary">
              <strong>Summary:</strong> {log.summary}
            </p>
          )}

          <div className={`actions${expandedIds[log._id] ? ' actions--expanded' : ''}`}>
            <button
              className={`transcript-toggle${expandedIds[log._id] ? ' is-expanded' : ''}`}
              onClick={() => toggleTranscript(log._id)}
            >
              {expandedIds[log._id] ? 'Hide Transcript' : 'Show Transcript'}
            </button>
          </div>

          {expandedIds[log._id] && log.transcript && (
            <pre className="transcript">{log.transcript}</pre>
          )}
        </div>
      );
    })
  );

  const handleToggleSavedReports = () => {
    const nextOpen = !savedReportsOpen;
    setSavedReportsOpen(nextOpen);
  };

  const handleViewSavedReport = async (reportId) => {
    try {
      setViewingReportId(reportId);
      const report = await loadReportDetail(reportId);
      setActiveReportView({
        source: 'saved',
        reportId,
        reportData: report.reportData,
        csvData: report.csvData || {},
      });
    } catch (err) {
      console.error('Failed to view saved report:', err);
      openNoticeModal({
        title: 'Unable to Open Report',
        message: err?.message || 'Failed to open saved report.',
      });
    } finally {
      setViewingReportId('');
    }
  };

  const handleDownloadSavedReportCsv = async (reportId, csvKey) => {
    try {
      setDownloadingReportAction(`${reportId}:${csvKey}`);
      const report = await loadReportDetail(reportId);
      const csvText = report.csvData?.[csvKey];
      if (!csvText) {
        throw new Error('CSV is not available for this report.');
      }

      downloadCsvText(
        csvText,
        buildGeneralAnalyticsCsvFilename(csvKey, report.reportData || report)
      );
    } catch (err) {
      console.error('Failed to download saved report CSV:', err);
      openNoticeModal({
        title: 'Unable to Download CSV',
        message: err?.message || 'Failed to download saved report CSV.',
      });
    } finally {
      setDownloadingReportAction('');
    }
  };

  const closeDeleteReportModal = () => {
    if (deleteReportState.deleting) return;
    setDeleteReportState({ open: false, report: null, deleting: false });
  };

  const handleDeleteSavedReport = async () => {
    const report = deleteReportState.report;
    if (!report?.id) {
      closeDeleteReportModal();
      return;
    }

    try {
      setDeleteReportState((currentState) => ({ ...currentState, deleting: true }));
      const { data } = await analysisReportsAPI.delete(report.id);
      if (!data?.success) {
        throw new Error(data?.message || 'Failed to delete report.');
      }

      setSavedReports((currentReports) => currentReports.filter((entry) => entry.id !== report.id));
      setCachedReports((currentReports) => {
        const nextReports = { ...currentReports };
        delete nextReports[report.id];
        return nextReports;
      });
      setActiveReportView((currentReportView) => (
        currentReportView?.reportId === report.id ? null : currentReportView
      ));
      setDeleteReportState({ open: false, report: null, deleting: false });
    } catch (err) {
      console.error('Failed to delete report:', err);
      setDeleteReportState((currentState) => ({ ...currentState, deleting: false }));
      openNoticeModal({
        title: 'Unable to Delete Report',
        message: err?.message || 'Failed to delete report.',
      });
    }
  };

  const hasCurrentStats = Boolean(activeReportView?.reportData);

  return (
    <div className={embeddedInWizard ? 'w-full space-y-4' : 'analytics-page'}>
      <div className="analytics-header w-full">
        {!embeddedInWizard && <h1 className="analytics-title">📊 General Analytics</h1>}
        <div className={`analytics-controls ${embeddedInWizard ? 'w-full' : 'w-full ga-controls-row'}`}>
          <div className={`script-selector ${embeddedInWizard ? 'flex-1 min-w-0' : 'ga-script-selector'}`}>
            <label>
              Script:
              {embeddedInWizard ? (
                <span className="script-select inline-flex items-center min-h-[34px]">
                  {selectedScriptName || fixedScriptName || 'No script selected'}
                </span>
              ) : (
                <select
                  className="script-select"
                  value={selectedScriptId}
                  onChange={(e) => {
                    setSelectedScriptId(e.target.value);
                    resetAnalysisState();
                  }}
                  disabled={!selectableScriptOptions.length || batchLoading}
                >
                  {selectableScriptOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                  {!selectableScriptOptions.length && (
                    <option value="">No scripts configured</option>
                  )}
                </select>
              )}
            </label>
            <DateRangePicker
              selectedPreset={selectedDatePreset}
              startDateInput={startDateInput}
              endDateInput={endDateInput}
              onPresetChange={applyPreset}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              disabled={batchLoading}
              error={dateRangeError}
              hint="Logs load for the selected script and inclusive date range."
            />
            <div className="flex flex-col gap-0.5">
              <div className="question-meta">
                <span className="font-semibold">Analytics type:</span> {formatAnalysisTypeLabel(selectedScriptType)}
              </div>
              <div className="question-meta">
                <span className="font-semibold">Engine:</span> {selectedEngine}
              </div>
            </div>
          </div>
          <div className={`flex flex-col gap-2 ${embeddedInWizard ? '' : 'ga-actions'}`}>
            <button
              className={`batch-button${batchLoading ? ' is-loading' : ''}${generatePulse ? ' batch-button--pulse' : ''}`}
              onClick={handleGenerateStats}
              disabled={batchLoading || Boolean(dateRangeError)}
            >
              <span className="batch-button__label">
                {batchLoading ? 'Generating stats...' : 'Generate Analysis'}
              </span>
            </button>
            {!embeddedInWizard && (
              <button
                className="batch-button batch-button--accent"
                onClick={handleToggleSavedReports}
                disabled={!selectedScriptId || batchLoading}
              >
                {savedReportsOpen ? 'Hide Reports' : 'View Reports'}
              </button>
            )}
            {shouldShowConfigureButton && (
              <button
                className="batch-button batch-button--accent"
                onClick={handleConfigureScriptSetup}
                disabled={!selectedScriptId || batchLoading}
              >
                Configure
              </button>
            )}
          </div>
        </div>
      </div>

      {loading && !hasCurrentStats && (
        <div className="empty-state">🔄 Loading call logs...</div>
      )}

      {batchLoading && batchProgress.total > 0 && (
        <div className="batch-progress">
          <div className="batch-progress__label">
            Processing {batchProgress.processed} / {batchProgress.total} calls
          </div>
          <div className="batch-progress__track">
            <div
              className="batch-progress__bar"
              style={{
                width: `${Math.min(
                  100,
                  batchProgress.total
                    ? Math.round((batchProgress.processed / batchProgress.total) * 100)
                    : 0
                )}%`,
              }}
            />
          </div>
        </div>
      )}

      {savedReportsOpen && !embeddedInWizard && (
        <div className="general-analytics-reports">
          <div className="general-analytics-reports__header">
            <div>
              <div className="general-analytics-reports__title">Saved Reports</div>
              <div className="general-analytics-reports__subtitle">
                Reports are listed for the selected script.
              </div>
            </div>
            <div className="general-analytics-reports__header-actions">
              <button
                type="button"
                className="general-analytics-reports__refresh"
                onClick={loadSavedReports}
                disabled={savedReportsLoading || !selectedScriptId}
              >
                {savedReportsLoading ? 'Refreshing...' : 'Refresh'}
              </button>
              <button
                type="button"
                className="general-analytics-reports__refresh"
                onClick={() => setSavedReportsOpen(false)}
              >
                Close
              </button>
            </div>
          </div>

          {savedReportsLoading && (
            <div className="empty-state">Loading saved reports...</div>
          )}

          {!savedReportsLoading && savedReportsError && (
            <div className="empty-state">{savedReportsError}</div>
          )}

          {!savedReportsLoading && !savedReportsError && savedReports.length === 0 && (
            <div className="empty-state">No saved reports found for this script.</div>
          )}

          {!savedReportsLoading && !savedReportsError && savedReports.length > 0 && (
            <div className="general-analytics-reports__list">
              {savedReports.map((report) => (
                <div key={report.id} className="general-analytics-reports__row">
                  <div className="general-analytics-reports__meta">
                    <div className="general-analytics-reports__date">
                      Generated at: {new Date(report.createdAt).toLocaleString()}
                    </div>
                    <div className="general-analytics-reports__range">
                      Report range: {formatSavedReportDateRange(report.dateRange) || 'No date range saved'}
                    </div>
                  </div>
                  <div className="general-analytics-reports__actions">
                    <button
                      type="button"
                      className="general-analytics-reports__action general-analytics-reports__action--view"
                      onClick={() => handleViewSavedReport(report.id)}
                      disabled={viewingReportId === report.id}
                    >
                      {viewingReportId === report.id ? 'Opening...' : 'View'}
                    </button>
                    {SURVEY_REPORT_CSV_OPTIONS
                      .filter((option) => Array.isArray(report.csvKeys) && report.csvKeys.includes(option.key))
                      .map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          className="general-analytics-reports__action"
                          onClick={() => handleDownloadSavedReportCsv(report.id, option.key)}
                          disabled={downloadingReportAction === `${report.id}:${option.key}`}
                        >
                          {downloadingReportAction === `${report.id}:${option.key}`
                            ? 'Downloading...'
                            : option.label}
                        </button>
                      ))}
                    <button
                      type="button"
                      className="general-analytics-reports__action general-analytics-reports__action--danger"
                      onClick={() => setDeleteReportState({ open: true, report, deleting: false })}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeReportView?.reportData && !batchLoading && (
        <div ref={reportAnchorRef}>
          <GeneralAnalyticsReportComponent
            reportData={activeReportView.reportData}
            csvData={activeReportView.csvData}
            downloadPulse={downloadPulse}
            onClose={() => setActiveReportView(null)}
          />
        </div>
      )}

      {!hasCurrentStats && !batchLoading && (
        <>
          {embeddedInWizard ? (
            <>
              <div className="call-card">
                {loading ? (
                  <div className="empty-state">🔄 Loading calls...</div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="call-title">
                      {activeCalls.length} calls available
                    </div>
                    <button
                      type="button"
                      className="transcript-toggle"
                      onClick={() => setShowCallDetails((prev) => !prev)}
                      disabled={activeCalls.length === 0}
                    >
                      {showCallDetails ? 'Hide Details' : 'Details'}
                    </button>
                  </div>
                )}
              </div>

              {showCallDetails && (
                <div data-scroll-container className="scroll-container">
                  {activeCalls.length === 0 ? (
                    <div className="empty-state">📭 No calls found for this script</div>
                  ) : (
                    renderCallCards()
                  )}
                </div>
              )}
            </>
          ) : (
            <div data-scroll-container className="scroll-container">
              {loading ? (
                <div className="empty-state">🔄 Loading calls...</div>
              ) : activeCalls.length === 0 ? (
                <div className="empty-state">📭 No calls found for this script</div>
              ) : (
                renderCallCards()
              )}
            </div>
          )}
        </>
      )}

      {noticeModal.open && (
        <div
          className="analytics-notice__backdrop"
          onClick={closeNoticeModal}
        >
          <div
            className="analytics-notice__content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="analytics-notice__title">{noticeModal.title}</div>
            <div className="analytics-notice__message">{noticeModal.message}</div>
            <div className="analytics-notice__actions">
              {noticeModal.showSetupAction && (
                <button
                  className="analytics-notice__button analytics-notice__button--primary"
                  onClick={goToAnalyticsSetup}
                >
                  Go to Analytics Setup
                </button>
              )}
              <button
                className="analytics-notice__button analytics-notice__button--secondary"
                onClick={closeNoticeModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteReportState.open && (
        <div
          className="analytics-notice__backdrop"
          onClick={closeDeleteReportModal}
        >
          <div
            className="analytics-notice__content"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="analytics-notice__title">Delete Report</div>
            <div className="analytics-notice__message">
              Delete the report from {deleteReportState.report?.createdAt ? new Date(deleteReportState.report.createdAt).toLocaleString() : 'this date'}? This cannot be undone.
            </div>
            <div className="analytics-notice__actions">
              <button
                className="analytics-notice__button analytics-notice__button--secondary"
                onClick={closeDeleteReportModal}
                disabled={deleteReportState.deleting}
              >
                Cancel
              </button>
              <button
                className="analytics-notice__button analytics-notice__button--danger"
                onClick={handleDeleteSavedReport}
                disabled={deleteReportState.deleting}
              >
                {deleteReportState.deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralAnalyticsComponent;
